// NotificationToast.jsx
// ─────────────────────────────────────────────────────────────────────────────
// iOS-style top banner notifications.
// • Slides in from the top, full-width banner
// • Swipe up or sideways to dismiss
// • TTS via Web Speech API (toggleable, language-aware)
// • Up to 3 toasts stacked

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import './NotificationToast.css';

// ── Zusätzliche Styles für den Dokument-Button im Toast (ergänzt NotificationToast.css)
// Wird einmalig beim ersten Import injiziert.
if (typeof document !== 'undefined' && !document.getElementById('nt-doc-btn-style')) {
  const s = document.createElement('style');
  s.id = 'nt-doc-btn-style';
  s.textContent = `
    .nt-action--document {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 6px;
      padding: 7px 13px;
      background: linear-gradient(135deg, #FF6407 0%, #ff8c42 100%);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 3px 10px rgba(255,100,7,0.30);
      letter-spacing: 0.01em;
      transition: opacity 0.15s;
    }
    .nt-action--document:hover { opacity: 0.88; }
    .nt-action--document svg { flex-shrink: 0; }
    .nt-action--fraghejvi {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      margin-top: 6px;
      padding: 7px 13px;
      background: linear-gradient(135deg, #6366F1 0%, #818CF8 100%);
      border: none;
      border-radius: 10px;
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 3px 10px rgba(99,102,241,0.30);
      letter-spacing: 0.01em;
      transition: opacity 0.15s;
    }
    .nt-action--fraghejvi:hover { opacity: 0.88; }
    .nt-action--fraghejvi svg { flex-shrink: 0; }
  `;
  document.head.appendChild(s);
}

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconCollection = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/>
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    <line x1="12" y1="12" x2="12" y2="16"/>
    <line x1="10" y1="14" x2="14" y2="14"/>
  </svg>
);

const IconAdmin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
    <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
    <line x1="6" y1="1" x2="6" y2="4"/>
    <line x1="10" y1="1" x2="10" y2="4"/>
    <line x1="14" y1="1" x2="14" y2="4"/>
  </svg>
);

const IconClose = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconArrow = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconDocument = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const IconQuestion = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconSpeaker = ({ active }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    {active ? (
      <>
        <line x1="23" y1="9" x2="17" y2="15"/>
        <line x1="17" y1="9" x2="23" y2="15"/>
      </>
    ) : (
      <>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
      </>
    )}
  </svg>
);

// ── Language → TTS locale map ─────────────────────────────────────────────────

const LANG_LOCALE = {
  de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES',
  ru: 'ru-RU', pl: 'pl-PL', ar: 'ar-SA', ro: 'ro-RO',
  tr: 'tr-TR', hr: 'hr-HR',
};

// ── Time formatter ────────────────────────────────────────────────────────────

function formatTime(createdAt, t, currentLanguage) {
  if (!createdAt) return '';
  const date    = new Date(createdAt.replace(' ', 'T'));
  const now     = new Date();
  const diffMin = Math.floor((now - date) / 60_000);

  if (diffMin < 1)  return t('notif_just_now') || 'Gerade eben';
  if (diffMin < 60) return `${diffMin} min`;

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24)   return `${diffH} h`;

  try {
    const locale = currentLanguage === 'ar' ? 'ar-SA' : `${currentLanguage}-${currentLanguage.toUpperCase()}`;
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
  } catch {
    return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
  }
}

// ── Single Toast ──────────────────────────────────────────────────────────────

function Toast({ notification, onDismiss, onMarkRead, onNavigate, stackIndex }) {
  const { t, currentLanguage } = useLanguage();

  // Animation phase: enter → idle → exit
  const [phase,      setPhase]      = useState('enter');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // Drag / swipe state
  const touchStartRef = useRef(null);
  const [drag, setDrag] = useState({ x: 0, y: 0, active: false });

  // Move to idle after enter animation finishes (prevents transform conflict)
  useEffect(() => {
    const id = setTimeout(() => setPhase(p => p === 'enter' ? 'idle' : p), 420);
    return () => clearTimeout(id);
  }, []);

  // Cancel TTS when unmounted
  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  // ── Mark as read (updates badge, toast stays visible) ────────────────────
  const handleMarkRead = useCallback(() => {
    onMarkRead?.(notification.id);
  }, [onMarkRead, notification.id]);

  // ── Dismiss: removes the toast AND marks as read ──────────────────────────
  const handleDismiss = useCallback(() => {
    if (phase === 'exit') return;
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
    setPhase('exit');
    setTimeout(() => onDismiss(notification.id), 320);
  }, [phase, onDismiss, notification.id]);

  // ── Swipe gesture ─────────────────────────────────────────────────────────

  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
    setDrag({ x: 0, y: 0, active: true });
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;

    if (dy < 0 || Math.abs(dx) > 15) {
      e.preventDefault();
      setDrag({ x: dx, y: Math.min(0, dy), active: true });
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const { x, y } = drag;
    // Swipe thresholds — only dismiss on deliberate gesture
    if (y < -55 || Math.abs(x) > 110) {
      handleDismiss();
    } else {
      setDrag({ x: 0, y: 0, active: false }); // snap back
    }
    touchStartRef.current = null;
  }, [drag, handleDismiss]);

  // ── TTS ──────────────────────────────────────────────────────────────────

  const hasTTS = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const handleSpeak = useCallback((e) => {
    e.stopPropagation();
    if (!hasTTS) return;
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const locTitle = notification['title_' + currentLanguage] || notification.title;
    const locBody  = notification['body_'  + currentLanguage] || notification.body;
    const text = [locTitle, locBody].filter(Boolean).join('. ');
    const utt  = new SpeechSynthesisUtterance(text);
    utt.lang   = LANG_LOCALE[currentLanguage] || 'de-DE';
    utt.rate   = 1.05;
    utt.onend  = () => setIsSpeaking(false);
    utt.onerror = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utt);
  }, [hasTTS, isSpeaking, notification.title, notification.body, currentLanguage]);

  // ── Navigate ─────────────────────────────────────────────────────────────

  const isCollection     = notification.type === 'new_element_in_collection';
  const isAdminMessage   = notification.type === 'admin_message';
  const isNewQuestion    = notification.type === 'unanswered_question'
                        || (isAdminMessage && notification.collection_hash === 'fraghejvi');
  // admin_message mit Dokument-Antwort (collection_hash = element hash_id)
  const isDocumentAnswer = isAdminMessage && !!notification.collection_hash && notification.collection_hash !== 'fraghejvi';
  // Styling: unanswered_question → Indigo (Admin-Style); admin_message + collection → Orange
  const useIndigoStyle   = notification.type === 'unanswered_question';

  const handleNavigate = useCallback(() => {
    if (!onNavigate || !isCollection || !notification.collection_hash) return;
    handleDismiss();
    setTimeout(() => onNavigate(`/collection/${notification.collection_hash}`), 330);
  }, [onNavigate, isCollection, notification.collection_hash, handleDismiss]);

  const handleNavigateToElement = useCallback(() => {
    if (!onNavigate || !notification.collection_hash) return;
    handleDismiss();
    setTimeout(() => onNavigate(`/element/${notification.collection_hash}`), 330);
  }, [onNavigate, notification.collection_hash, handleDismiss]);

  // Direkt zum FragHejVi-Tab im AdminPanel navigieren.
  // sessionStorage wird VOR navigate gesetzt – AdminPanel liest es beim Mount.
  const handleNavigateToFragHejVi = useCallback(() => {
    if (!onNavigate) return;
    handleDismiss();
    setTimeout(() => {
      sessionStorage.setItem('hejvi_admin_tab', 'fraghejvi');
      onNavigate('/admin');
    }, 330);
  }, [onNavigate, handleDismiss]);

  // ── Drag visual feedback ──────────────────────────────────────────────────

  const dragDist    = Math.sqrt(drag.x ** 2 + drag.y ** 2);
  const dragOpacity = drag.active ? Math.max(0.2, 1 - dragDist / 160) : undefined;
  const dragTransform = drag.active
    ? `translateY(${drag.y}px) translateX(${drag.x}px) rotate(${drag.x * 0.025}deg)`
    : undefined;

  const inlineStyle = {
    '--stack-index': stackIndex,
    // ── Kein CSS-Auto-Dismiss: Im idle-Zustand explizit animation:none setzen,
    // damit keine CSS-Keyframe-Animation den Toast automatisch ausblendet.
    // Toasts bleiben sichtbar bis der Nutzer sie explizit schliesst.
    ...(phase === 'idle' && !drag.active
      ? { animation: 'none', transition: 'none', opacity: 1 }
      : {}),
    ...(drag.active
      ? { transform: dragTransform, opacity: dragOpacity, transition: 'none' }
      : {}),
  };

  const cssClass = [
    'nt-toast',
    `nt-toast--${phase}`,
    useIndigoStyle ? 'nt-toast--admin' : '',  // indigo nur für unanswered_question
    drag.active ? 'nt-toast--dragging' : '',
    stackIndex > 0 ? 'nt-toast--stacked' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cssClass}
      style={inlineStyle}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="alert"
      aria-live="polite"
    >
      {/* Swipe affordance handle */}
      <div className="nt-drag-handle" aria-hidden="true" />

      <div
        className="nt-body-row"
        onClick={() => setDetailOpen(true)}
        style={{ cursor: 'pointer' }}
      >
        {/* Left icon */}
        <div className={`nt-icon ${useIndigoStyle ? 'nt-icon--admin' : 'nt-icon--collection'}`}>
          {(useIndigoStyle || isAdminMessage) ? <IconAdmin /> : <IconCollection />}
        </div>

        {/* Content */}
        <div className="nt-content">
          <div className="nt-header-row">
            <span className="nt-type-label">
              {notification.type === 'unanswered_question'
                ? (t('notif_type_new_question') || 'Neue Frage')
                : isAdminMessage
                  ? (t('notif_type_message')     || 'Admin-Nachricht')
                  : (t('notif_type_new_content') || 'Neuer Inhalt')}
            </span>
            <span className="nt-time">
              {formatTime(notification.created_at, t, currentLanguage)}
            </span>
          </div>

          <p className="nt-title">{notification['title_' + currentLanguage] || notification.title}</p>
          <p className="nt-body-text">{notification['body_' + currentLanguage] || notification.body}</p>

          {isCollection && notification.collection_hash && onNavigate && (
            <button className="nt-action" onClick={(e) => { e.stopPropagation(); handleNavigate(); }} type="button">
              {t('notif_goto_collection') || 'Zur Sammlung'} <IconArrow />
            </button>
          )}

          {isDocumentAnswer && onNavigate && (
            <button
              className="nt-action nt-action--document"
              onClick={(e) => { e.stopPropagation(); handleNavigateToElement(); }}
              type="button"
            >
              <IconDocument />
              {t('notif_goto_document') || 'Zum Dokument'}
              <IconArrow />
            </button>
          )}

          {isNewQuestion && onNavigate && (
            <button
              className="nt-action nt-action--fraghejvi"
              onClick={(e) => { e.stopPropagation(); handleNavigateToFragHejVi(); }}
              type="button"
            >
              {/* Shield icon für Admin-Kontext */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              {notification.type === 'unanswered_question'
                ? (t('notif_goto_adminpanel') || 'Zum Admin-Panel')
                : (t('notif_goto_fraghejvi') || 'FragHejVi öffnen')}
              <IconArrow />
            </button>
          )}
        </div>

        {/* TTS + Close — stopPropagation so they never open the detail modal */}
        <div className="nt-controls" onClick={e => e.stopPropagation()}>
          {hasTTS && (
            <button
              className={`nt-speak${isSpeaking ? ' nt-speak--active' : ''}`}
              onClick={handleSpeak}
              type="button"
              aria-label={isSpeaking ? t('notif_tts_stop') || 'Vorlesen stoppen' : t('notif_tts_read') || 'Vorlesen'}
              title={isSpeaking ? t('notif_tts_stop') || 'Stoppen' : t('notif_tts_read') || 'Vorlesen'}
            >
              <IconSpeaker active={isSpeaking} />
            </button>
          )}
          <button
            className="nt-close"
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            type="button"
            aria-label={t('notif_dismiss_all') || 'Schließen'}
          >
            <IconClose />
          </button>
        </div>
      </div>

      {/* Accent bar at bottom */}
      <div className="nt-bar" />

      {/* ── Detail Modal — via Portal in document.body ──────────────────────
           Grund: position:fixed innerhalb eines Elements mit CSS-transform
           (die Slide-in-Animation des Toasts) bezieht sich nicht auf den
           Viewport, sondern auf den transformierten Eltern-Container.
           createPortal rendert das Modal direkt in <body> und umgeht so
           jeden Stacking-Context der Toast-Animation.
      ──────────────────────────────────────────────────────────────────── */}
      {detailOpen && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9600,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
          }}
          onClick={() => { setDetailOpen(false); handleDismiss(); }}
        >
          <div
            style={{
              background: '#fff', borderRadius: '20px',
              width: '100%', maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              fontFamily: "'Inter', sans-serif",
              /* Flex-Column: Header + Footer bleiben fixiert, Body scrollt */
              display: 'flex', flexDirection: 'column',
              maxHeight: 'calc(100dvh - 40px)',
              overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header — immer sichtbar */}
            <div style={{
              flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 18px 14px', borderBottom: '1px solid #F3F4F6',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: useIndigoStyle ? '#EEF2FF' : '#FFF7ED',
                  border: `1px solid ${useIndigoStyle ? '#C7D2FE' : '#FDBA74'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: useIndigoStyle ? '#6366F1' : '#FF6407',
                }}>
                  {(useIndigoStyle || isAdminMessage) ? <IconAdmin /> : <IconCollection />}
                </div>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                  {notification['title_' + currentLanguage] || notification.title}
                </p>
              </div>
              <button
                style={{
                  background: '#F3F4F6', border: 'none', borderRadius: '50%',
                  width: 30, height: 30, cursor: 'pointer', flexShrink: 0, marginLeft: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#6B7280',
                }}
                onClick={() => { setDetailOpen(false); handleDismiss(); }}
              >
                <IconClose />
              </button>
            </div>

            {/* Body — scrollbar bei langem Inhalt */}
            <div style={{
              flex: 1, overflowY: 'auto',
              padding: '16px 18px 20px',
              WebkitOverflowScrolling: 'touch',
            }}>
              <p style={{ margin: 0, fontSize: 15, color: '#374151', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                {notification['body_' + currentLanguage] || notification.body}
              </p>
              {notification.created_at && (
                <p style={{ margin: '12px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                  {formatTime(notification.created_at, t, currentLanguage)}
                </p>
              )}
            </div>

            {/* Footer — immer sichtbar */}
            <div style={{
              flexShrink: 0,
              padding: '12px 18px 18px', display: 'flex', flexDirection: 'column', gap: 8,
              borderTop: '1px solid #F3F4F6',
            }}>
              {/* FragHejVi / Admin-Panel-Button — primäre Aktion für neue Fragen */}
              {isNewQuestion && onNavigate && (
                <button
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)',
                    border: 'none', borderRadius: 12,
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    padding: '13px 20px', cursor: 'pointer', width: '100%',
                    boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
                    letterSpacing: '0.01em',
                  }}
                  onClick={() => { setDetailOpen(false); handleNavigateToFragHejVi(); }}
                >
                  {/* Shield für Admin, Question für legacy */}
                  {notification.type === 'unanswered_question' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  ) : (
                    <IconQuestion />
                  )}
                  {notification.type === 'unanswered_question'
                    ? (t('notif_goto_adminpanel') || 'Zum Admin-Panel')
                    : (t('notif_goto_fraghejvi') || 'FragHejVi öffnen')}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              )}

              {/* Dokument-Button — prominent als primäre Aktion */}
              {isDocumentAnswer && onNavigate && (
                <button
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    background: 'linear-gradient(135deg, #FF6407 0%, #ff8c42 100%)',
                    border: 'none', borderRadius: 12,
                    color: '#fff', fontSize: 14, fontWeight: 700,
                    padding: '13px 20px', cursor: 'pointer', width: '100%',
                    boxShadow: '0 4px 14px rgba(255,100,7,0.35)',
                    letterSpacing: '0.01em',
                  }}
                  onClick={() => { setDetailOpen(false); handleNavigateToElement(); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  {t('notif_open_document') || 'Dokument öffnen'}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {isCollection && notification.collection_hash && onNavigate && (
                  <button
                    style={{
                      background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 10,
                      color: '#FF6407', fontSize: 13, fontWeight: 600, padding: '9px 16px', cursor: 'pointer',
                    }}
                    onClick={() => { setDetailOpen(false); handleNavigate(); }}
                  >
                    {t('notif_goto_collection') || 'Zur Sammlung'} →
                  </button>
                )}
                <button
                  style={{
                    background: '#F3F4F6', border: 'none', borderRadius: 10,
                    color: '#374151', fontSize: 13, fontWeight: 600, padding: '9px 16px', cursor: 'pointer',
                  }}
                  onClick={() => { setDetailOpen(false); handleDismiss(); }}
                >
                  {t('notif_close') || 'Schließen'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Module-level persistent state ─────────────────────────────────────────────
//
// WARUM module-level statt component state + sessionStorage:
//
// Jede Seite (Dashboard, Profile, MyCourses …) mountet ihre eigene
// <NotificationToast>-Instanz. Beim Navigieren Dashboard → Profile:
//   1. Dashboard unmountet → React-State verloren
//   2. Profile mountet  → liest React-State neu (leer)
//
// Module-level Variablen überleben jeden Component-Mount/Unmount solange
// das JS-Bundle geladen bleibt. sessionStorage = Fallback für echte Refreshes.
//
// → Toasts bleiben sichtbar bis der Nutzer sie explizit schliesst.

const _seenIds = new Set();
const _QKEY    = 'hejvi_notif_q';

// Einmalig beim Modul-Load aus sessionStorage befuellen (Page-Refresh-Recovery)
const _q = (() => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(_QKEY) || '[]');
    if (Array.isArray(saved)) {
      saved.forEach(n => _seenIds.add(n.id));
      return saved;
    }
  } catch { /* ignore */ }
  return [];
})();

// _q in-place aendern UND sessionStorage synchron halten
function _setQ(next) {
  _q.splice(0, _q.length, ...next);
  try { sessionStorage.setItem(_QKEY, JSON.stringify(next)); } catch { /* ignore */ }
}

// ── Toast Stack (queue-based: one at a time) ──────────────────────────────────

export default function NotificationToast({ notifications, onDismiss, onDismissAll, onNavigate }) {
  const { t } = useLanguage();

  // Initialisierung aus _q (module-level) — bereits befuellt auch nach Navigation
  const [queue, setQueueState] = useState(() => [..._q]);

  // Wrapper: haelt React-State, _q und sessionStorage synchron
  const setQueue = useCallback((updater) => {
    setQueueState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      _setQ(next);
      return next;
    });
  }, []);

  // Ref: wurde mindestens einmal eine nicht-leere Notification-Liste vom Server empfangen?
  // Verhindert, dass beim initialen Mount (notifications=[]) alle _q-Items fälschlich
  // gepruned werden, bevor der SSE-Init-Event angekommen ist.
  const hasReceivedData = React.useRef(_q.length > 0); // true wenn aus sessionStorage wiederhergestellt

  // Neue Notifications in Queue aufnehmen + veraltete (server-seitig gelöscht) entfernen
  useEffect(() => {
    // Noch kein Init-Event empfangen und auch kein sessionStorage-Restore → warten
    if (!hasReceivedData.current && notifications.length === 0) return;
    hasReceivedData.current = true;

    const serverIds = new Set(notifications.map(n => n.id));

    // 1. Stale Items aus Queue entfernen — Notifications die der Server nicht mehr
    //    liefert (z.B. zugehörige Frage wurde gelöscht). _seenIds ebenfalls bereinigen,
    //    damit das Item neu angezeigt wird falls es vom Server wieder auftaucht.
    setQueue(prev => {
      const pruned = prev.filter(n => serverIds.has(n.id));
      if (pruned.length === prev.length) return prev; // nichts zu tun
      prev.filter(n => !serverIds.has(n.id)).forEach(n => _seenIds.delete(n.id));
      return pruned;
    });

    // 2. Neue Items hinzufügen
    const newItems = notifications.filter(n => !_seenIds.has(n.id));
    if (newItems.length === 0) return;
    newItems.forEach(n => _seenIds.add(n.id));
    setQueue(prev => [...prev, ...newItems]);
  }, [notifications, setQueue]);

  const current   = queue[0] ?? null;
  const remaining = queue.length - 1;

  // Entfernt Toast aus Queue UND markiert als gelesen im Backend
  const handleDismissFromQueue = useCallback((id) => {
    onDismiss(id);
    setQueue(prev => prev.filter(n => n.id !== id));
  }, [onDismiss, setQueue]);

  // Nur als gelesen markieren (Badge -1) — Toast bleibt sichtbar
  const handleMarkReadOnly = useCallback((id) => {
    onDismiss(id);
  }, [onDismiss]);

  // Alle Toasts schließen — nutzt onDismissAll für einen einzigen Batch-API-Call
  // statt N einzelner POST /notifications/dismiss Requests.
  const handleDismissAll = useCallback(() => {
    const ids = queue.map(n => n.id);
    onDismissAll(ids);
    setQueue([]);
  }, [queue, onDismissAll, setQueue]);

  if (!current) return null;

  return (
    <div className="nt-stack" aria-label={t('adm_notifications') || 'Benachrichtigungen'}>
      <Toast
        key={current.id}
        notification={current}
        onDismiss={handleDismissFromQueue}
        onMarkRead={handleMarkReadOnly}
        onNavigate={onNavigate}
        stackIndex={0}
      />
      {remaining > 0 && (
        <div className="nt-queue-hint">
          <button
            className="nt-dismiss-all"
            onClick={handleDismissAll}
            type="button"
          >
            Alle schließen ({queue.length})
          </button>
          <span className="nt-queue-count">+{remaining} weitere</span>
        </div>
      )}
    </div>
  );
}
